import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable, retry, throwError, timer } from 'rxjs';
import { Injectable } from '@angular/core';
import { appInjector } from '../../../app-injector';
import { NotificationService } from '@core/services/notification.service';
import { NotificationType } from '@enum/common/notification-type.enum';

@Injectable()
export class RetryInterceptor {

  private readonly MAX_NUMBER_OF_RETRY_NO_CONNECTION: number = 5 + 1;
  private readonly RETRY_DELAY: number = 500;

  /**
   * Só métodos idempotentes são repetidos.
   *
   * `status 0` significa que o navegador não recebeu resposta — e NÃO que o
   * servidor não recebeu a requisição: a queda pode ter acontecido no caminho de
   * volta, com o lançamento já gravado. Repetir um POST/PUT/PATCH/DELETE nessa
   * situação é lançamento, baixa ou pagamento duplicado, até 6 vezes e sem
   * nenhum sinal na tela. GET e HEAD podem ser repetidos porque não têm efeito.
   */
  private readonly IDEMPOTENT_METHODS: readonly string[] = ['GET', 'HEAD'];

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      retry({
        count: this.MAX_NUMBER_OF_RETRY_NO_CONNECTION,
        delay: (error: HttpErrorResponse, retryAttempt: number): Observable<number> => {

          if (error.status !== 0) {
            return throwError(() => error);
          }

          // A ORDEM AQUI É DELIBERADA, e tem um preço conhecido.
          //
          // A guarda de método vem ANTES da guarda de tentativas, e é a
          // tentativa esgotada que abre o aviso "Problemas de conexão".
          // Consequência: uma ESCRITA com a rede caída sai por aqui na primeira
          // falha e NÃO recebe esse aviso — o `HttpErrorResponse` de `status 0`
          // segue para o consumidor, que o traduz pelo caminho comum e mostra o
          // genérico ("Erro desconhecido", ou o do outro tradutor). O usuário
          // fica sabendo que falhou, mas não que o motivo foi a internet.
          //
          // Isso é um TRADEOFF ACEITO, não um descuido, e não deve ser
          // "corrigido" movendo esta guarda para depois da outra: o aviso de
          // conexão só é honesto depois das seis tentativas, e a escrita não
          // pode ter nenhuma. Repetir POST/PUT/PATCH/DELETE em `status 0` é
          // lançamento, baixa ou pagamento duplicado, porque `status 0` não
          // distingue "não chegou ao servidor" de "chegou e a resposta se
          // perdeu na volta" (BUG-CLI-04/70).
          //
          // Se um dia o aviso precisar aparecer também na escrita, o lugar é um
          // ramo PRÓPRIO aqui — notificar e relançar sem agendar retry — nunca
          // a troca de ordem.
          if (!this.IDEMPOTENT_METHODS.includes(request.method.toUpperCase())) {
            return throwError(() => error);
          }

          if (retryAttempt >= this.MAX_NUMBER_OF_RETRY_NO_CONNECTION) {
            const notification = appInjector().get(NotificationService);
            notification.open('Problemas de conexão. Verifique sua internet, por favor.', NotificationType.DANGER);
            return throwError(() => error);
          }

          console.log(`Attempt ${retryAttempt}: retrying in ${retryAttempt * this.RETRY_DELAY}ms`);
          // retry after 1s, 2s, etc...
          return timer(retryAttempt * this.RETRY_DELAY);
        },
      }),
    );
  }
}
